import React, { useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';

import useMyBoards from '@hooks/Board/useMyBoards';
import AddToBoardModalContext, { IAddToBoardModal } from '@context/Modal/add-to-board-modal-context';
import LoginRegisterModalContext from '@context/Modal/login-register-modal-context';
import UserContext from '@context/User/user-context';
import { IUserBoard } from '@components/shared/modals/AddToBoardModal/AddToBoardModal';
import useMutationCreateCustomerBoard from '@hooks/Board/useMutationCreateCustomerBoard';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import useAlert, { TYPES } from '@hooks/useAlert/useAlert';
import boardItemTypes from '@constants/boardItemTypes';
import addCustomerBoardAsset from '@graphql/Board/mutations/addCustomerBoardAsset';
import addCustomerBoardVendorVariant from '@graphql/Board/mutations/addCustomerBoardVendorVariant';
import dynamic from 'next/dynamic';
import SaveSvg from '../../../../public/static/save.svg';

import styles from './AddToBoardModalProvider.module.scss';

const AddToBoardModal = dynamic(
  () => import('@components/shared/modals/AddToBoardModal/AddToBoardModal'),
  { ssr: false }
);

const SESSION_STORAGE_KEY = 'havenly.pendingAddToBoard';

const AddToBoardModalProvider:React.FunctionComponent = ({ children }) => {
  const [
    busy,
    setBusy,
  ] = useState(false);
  const [
    modalState,
    setModalState,
  ] = useState<IAddToBoardModal | null>(null);
  const { myBoards } = useMyBoards();
  const { open } = useContext(LoginRegisterModalContext);
  const router = useRouter();
  const {
    addSavedAssets, addSavedVendorVariants, user, isLoading,
  } = useContext(UserContext);

  const [
    mutationCreateCustomerBoard,
  ] = useMutationCreateCustomerBoard();
  const [
    mutationAddCustomerBoardAsset,
  ] = useMutation(addCustomerBoardAsset);

  const [
    mutationAddCustomerBoardVendorVariant,
  ] = useMutation(addCustomerBoardVendorVariant);

  const alert = useAlert(TYPES.SUCCESS);
  const errorAlert = useAlert(TYPES.ERROR);

  useEffect(() => {
    const pendingAddToSession = window.sessionStorage
      .getItem(`${SESSION_STORAGE_KEY} ${window.location.href}`);

    if (pendingAddToSession) {
      window.sessionStorage.removeItem(`${SESSION_STORAGE_KEY} ${window.location.href}`);
      setModalState(JSON.parse(pendingAddToSession));
    }
  }, []);

  useEffect(() => {
    if (modalState && !isLoading && !user) {
      open({
        closable: true,
        onLoginSuccess: () => {},
        onRegisterSuccess: () => {},
        magicLinkRedirectUrl: router.asPath,
        source: modalState.source,
      });
    }
  }, [user, isLoading, modalState]);

  const handleOnSave = async (board: IUserBoard) => {
    let variables = {};
    let saveMutation;
    if (modalState!.saveItemType === boardItemTypes.ASSETS) {
      saveMutation = mutationAddCustomerBoardAsset;
      variables = {
        customerBoardId: board.id,
        sourceBoardId: modalState?.sourceBoard?.id,
        assetId: modalState!.saveItemId,
      };
    } else if (modalState!.saveItemType === boardItemTypes.VENDOR_VARIANTS) {
      saveMutation = mutationAddCustomerBoardVendorVariant;
      variables = {
        customerBoardId: board.id,
        sourceBoardId: modalState?.sourceBoard?.id,
        vendorVariantId: modalState!.saveItemId,
      };
    } else {
      errorAlert(`Failed to save to ${board.name}.`, 3000);
      setModalState(null);
      return;
    }

    let results;
    try {
      results = await saveMutation({
        variables,
      });
    } catch (err) {
      errorAlert(`Failed to save to ${board.name}.`, 3000);
      return;
    }

    if (results && results.data) {
      if (modalState!.saveItemType === boardItemTypes.ASSETS) {
        addSavedAssets(modalState!.saveItemId);

        if (modalState?.sourceBoard) {
          analyticsTrack(
            'explore_designer_board_saved',
            {
              boardId: modalState.sourceBoard.id,
              designerId: modalState.sourceBoard.designer?.id,
              saved_from: window.location.pathname,
              style_array: modalState.sourceBoard.subStyles?.map(({ name }) => name).join(', ') || [],
            },
          );
        }
      } else if (modalState!.saveItemType === boardItemTypes.VENDOR_VARIANTS) {
        addSavedVendorVariants(modalState!.saveItemId);
      }
      const alertMessage = (
        <div className={ styles.AlertWrapper }>
          <span className={ styles.SaveIcon }>
            <SaveSvg />
          </span>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {`Saved to ${board.name}!`}
        </div>
      );

      alert(alertMessage, 3000);
      setModalState(null);
    }
  };

  const handleOnSaveAndCreateBoard = async (boardName: string) => {
    setBusy(true);
    let result;
    try {
      result = await (mutationCreateCustomerBoard as any)(
        {
          name: boardName,
          items: [{
            id: modalState!.saveItemId,
            model: modalState!.saveItemType,
          }],
        },
      );
    } catch (err) {
      errorAlert(`Failed to create ${boardName}.`, 3000);
      setBusy(false);
      return;
    }
    if (result.data && result.data.createCustomerBoard) {
      if (modalState!.saveItemType === boardItemTypes.ASSETS) {
        addSavedAssets(modalState!.saveItemId);
      } else if (modalState!.saveItemType === boardItemTypes.VENDOR_VARIANTS) {
        addSavedVendorVariants(modalState!.saveItemId);
      }

      const newBoard = result.data.createCustomerBoard;
      analyticsTrack(
        'explore_board_created',
        {
          boardId: newBoard.id,
        },
      );

      const alertMessage = (
        <>
          {`Created ${newBoard.name}.`}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <a className={ styles.AlertLink } href={ `/home/boards/${newBoard.id}` }>
            View
          </a>
        </>
      );

      alert(alertMessage, 3000);
      setModalState(null);
    } else {
      errorAlert(`Failed to create ${boardName}.`, 3000);
    }
    setBusy(false);
  };

  return (
    <AddToBoardModalContext.Provider
      value={{
        open: setModalState,
      }}
    >
      {modalState && user && (
        <AddToBoardModal
          {...modalState}
          busy={busy}
          userBoards={myBoards}
          onClose={() => {
            setModalState(null);
          }}
          onSave={ handleOnSave }
          onSaveAndCreateBoard={ handleOnSaveAndCreateBoard }
        />
      )}
      {children}
    </AddToBoardModalContext.Provider>
  );
};

export default AddToBoardModalProvider;
